import React from "react";
import { useState, useEffect, useMemo } from "react";
import IndividualAccount from "./IndividualAccount";
import individualicon from "../assets/images/icons/individualicon.svg";
import businessicon from "../assets/images/icons/businessicon.svg";
import BusinessAccount from "./BusinessAccount";
import logo from "../assets/logo/Logo.svg";
import google from "../assets/logo/Google.svg";
import facebook from "../assets/logo/Facebook.svg";
import Carousel from "../components/carousel/logincarousel";
import loginbanner from "../assets/images/login-banner.png";
import loginbannertwo from "../assets/images/login-banner-2.png";
import loginbannerthree from "../assets/images/login-banner-3.png";
import loginbannerfour from "../assets/images/login-banner-4.png";
import loginbannersm from "../assets/images/login-banner-sm.png";
import bannerlayout from "../assets/images/banner-layout.png";
import { Menu, MenuItem, MenuItems } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import countryList from "react-select-country-list";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { startUserSession, clearUserSession, getStoredToken } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export default function SignupForm() {
  const [accountType, setAccountType] = useState("individual");

  const navigate = useNavigate(); // Initialize navigation

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    dob: "",
    password: "",
    confirmPassword: "",
    gender: "",
    termsAccepted: false,
    businessName: "",
    ownerName: "",
    incorporationDate: "",
    location: "",
    country: "",
    language: "",
    mobile: "",
    countryCode: "+91", // Default country code
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(""); // New error state for password validation
  const [confirmPasswordError, setConfirmPasswordError] = useState(""); // Error for confirm password
  const [isValid, setIsValid] = useState(true); // ✅ Track form validity
  const [token, setToken] = useState("");
  const [userData, setUserData] = useState(null);
  const [errors, setErrors] = useState({});

  // Country list options provided by react-select-country-list
  const countryOptionsList = useMemo(() => countryList().getData(), []);

  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // "email" → "phone"

  const [email, setEmail] = useState("");
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState("");
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);

  const [timer, setTimer] = useState(300); // 5 minutes (300 seconds)
  const [canResend, setCanResend] = useState(false);
  const [timerInterval, setTimerInterval] = useState(null); // Store interval ID
  const [termsOpened, setTermsOpened] = useState(false); // Track if terms were opened

  useEffect(() => {
    if (formData.password) {
      validatePassword(formData.password);
    }
  }, []);

  // Load form data from sessionStorage on component mount
  useEffect(() => {
    const savedFormData = sessionStorage.getItem("signupFormData");
    const savedAccountType = sessionStorage.getItem("signupAccountType");
    if (savedAccountType) {
      setAccountType(savedAccountType);
    }
    if (savedFormData) {
      const parsed = JSON.parse(savedFormData);
      setFormData(parsed);

      if (parsed.password) {
        validatePassword(parsed.password); // ✅ Re-validate on reload
      }
    }
  }, []);

  useEffect(() => {
    const termsAccepted =
      sessionStorage.getItem("termsAccepted") || formData.termsAccepted;
    setTermsOpened(termsAccepted === "true");
  }, [formData.termsAccepted]);
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const checkUsernameAvailability = async (username) => {
    if (!username) {
      setUsernameAvailable(null);
      setUsernameMessage("");
      setUsernameSuggestions([]);
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/verify-username`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username }),
        }
      );

      const data = await response.json();
      console.log("Username Check Response:", data);

      if (response.ok && data.success) {
        if (data.result.available) {
          setUsernameAvailable(true);
          setUsernameMessage("Username is available ✅");
          setUsernameSuggestions([]);
        } else {
          setUsernameAvailable(false);
          setUsernameMessage("Username is already taken ❌");
          setUsernameSuggestions(data.result.suggestions || []);
        }
      } else {
        setUsernameAvailable(null);
        setUsernameMessage("Error checking username.");
      }
    } catch (error) {
      console.error("Error:", error);
      setUsernameMessage("Network error. Try again.");
    }
  };

  // Function to get user location and convert to address
  const getLocation = async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      const data = await response.json();
      const address = `${data.city}, ${data.region}, ${data.country_name}`;
      setLocation(address);
      setFormData((prev) => ({ ...prev, location: address }));
    } catch (err) {
      setError("Failed to fetch address.");
    }
  };

  const handleSubmitVerify = async (e) => {
    e.preventDefault();
    const storedEmail = sessionStorage.getItem("userEmail");
    if (!storedEmail) {
      toast.error("No email found. Please sign up again.");
      setErrors((prev) => ({
        ...prev,
        email: "No email found. Please sign up again.",
      }));
      return;
    }
    const requestData = {
      email: storedEmail, // Email entered by the user
      otp: code, // Verification code entered by the user
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/verify-credential`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        console.log("Verification Successful:", data);
        toast.success("✅ Verification Successful! Please log in.");

        // Redirect to login page
        navigate("/login");
      } else {
        console.error("Verification Failed:", data);
        toast.error(
          `❌ Verification failed: ${
            data.result?.message || "Invalid code. Please try again."
          }`
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("❌ Something went wrong. Please try again.");
    }
  };

  // const handleResendOTP = async (e) => {
  //   e.preventDefault();

  //   const storedEmail = sessionStorage.getItem("userEmail");

  //   if (!storedEmail) {
  //     alert("No email found. Please sign up again.");
  //     return;
  //   }

  //   const requestData = {
  //     email: storedEmail, // Email for resending OTP
  //   };

  //   try {
  //     const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify(requestData),
  //     });

  //     const data = await response.json();
  //     console.log("Resend OTP Response:", data);

  //     if (response.ok && data.success) {
  //       alert("OTP has been resent to your email.");
  //       startTimer(); // Restart the timer when OTP is resent

  //     } else {
  //       alert(`Failed to resend OTP: ${data.result?.message || "Please try again."}`);
  //     }
  //   } catch (error) {
  //     console.error("Error:", error);
  //     alert("Something went wrong. Please try again.");
  //   }
  // };

  const handleResendOTP = async (e) => {
    e.preventDefault();

    let requestData = {};

    if (selectedMethod === "email") {
      const storedEmail = sessionStorage.getItem("userEmail");
      if (!storedEmail) {
        toast.error("No email found. Please sign up again.");
        setErrors((prev) => ({
          ...prev,
          email: "No email found. Please sign up again.",
        }));
        return;
      }
      requestData = { email: storedEmail };
    } else if (selectedMethod === "phone") {
      if (!formData.mobile) {
        toast.error("Please enter your mobile number.");
        setErrors((prev) => ({
          ...prev,
          mobile: "Please enter your mobile number.",
        }));
        return;
      }
      const formattedMobile = `${formData.countryCode}-${formData.mobile}`;
      requestData = { mobile_number: formattedMobile };
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        storeQaOtp(selectedMethod, data);
        toast.success("OTP has been resent successfully.");
        startTimer(); // ✅ Restart the timer
      } else {
        const msg =
          data.result?.message || "Failed to resend OTP. Please try again.";
        toast.error(msg);
        if (msg.toLowerCase().includes("email"))
          setErrors((prev) => ({ ...prev, email: msg }));
        else if (
          msg.toLowerCase().includes("mobile") ||
          msg.toLowerCase().includes("phone")
        )
          setErrors((prev) => ({ ...prev, mobile: msg }));
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      toast.error("Something went wrong while resending OTP.");
    }
  };

  console.log("Form Data on OTP Generation:", formData);

  const handleGenerateOTP = async (e) => {
    e.preventDefault();
    setShowOTPModal(true);
    // Send OTP to email
    const storedEmail = formData.email;
    if (!storedEmail) {
      toast.error("No email found. Please sign up again.");
      setErrors((prev) => ({
        ...prev,
        email: "No email found. Please sign up again.",
      }));
      return;
    }

    const requestData = { email: storedEmail };

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        storeQaOtp("email", data);
        toast.success("OTP has been sent to your email.");
      } else {
        const msg =
          data.result?.message || "Failed to send OTP. Please try again.";
        toast.error(msg);
        if (msg.toLowerCase().includes("email"))
          setErrors((prev) => ({ ...prev, email: msg }));
        else if (
          msg.toLowerCase().includes("mobile") ||
          msg.toLowerCase().includes("phone")
        )
          setErrors((prev) => ({ ...prev, mobile: msg }));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    }
    // } else if (selectedMethod === "phone") {
    //   // Send OTP to mobile number
    //   const formattedMobile = `${formData.countryCode}-${formData.mobile}`;

    //   if (!formData.mobile) {
    //     toast.error("Please enter your mobile number.");
    //     setErrors((prev) => ({
    //       ...prev,
    //       mobile: "Please enter your mobile number.",
    //     }));
    //     return;
    //   }

    //   const requestData = { mobile_number: formattedMobile };

    //   try {
    //     const response = await fetch(
    //       `${API_BASE_URL}/auth/send-otp`,
    //       {
    //         method: "POST",
    //         headers: { "Content-Type": "application/json" },
    //         body: JSON.stringify(requestData),
    //       }
    //     );

    //     const data = await response.json();
    //     if (response.ok && data.success) {
    //       toast.success(
    //         `OTP has been sent to your mobile number: ${formattedMobile}`
    //       );
    //     } else {
    //       const msg =
    //         data.result?.message || "Failed to send OTP. Please try again.";
    //       toast.error(msg);
    //       if (msg.toLowerCase().includes("email"))
    //         setErrors((prev) => ({ ...prev, email: msg }));
    //       else if (
    //         msg.toLowerCase().includes("mobile") ||
    //         msg.toLowerCase().includes("phone")
    //       )
    //         setErrors((prev) => ({ ...prev, mobile: msg }));
    //     }
    //   } catch (error) {
    //     console.error("Error:", error);
    //     toast.error("Something went wrong. Please try again.");
    //   }
    // }
  };

  // const handleVerifyOTP = async (e) => {
  //   e.preventDefault();

  //   if (!code) {
  //     alert("Please enter the OTP.");
  //     return;
  //   }

  //   let requestData = {};

  //   if (selectedMethod === "email") {
  //     const storedEmail = sessionStorage.getItem("userEmail");
  //     if (!storedEmail) {
  //       alert("No email found. Please sign up again.");
  //       return;
  //     }
  //     requestData = { email: storedEmail, otp: code };

  //   } else if (selectedMethod === "phone") {
  //     const formattedMobile = `${formData.countryCode}-${formData.mobile}`;
  //     requestData = { mobile_no: formattedMobile, otp: code };
  //   }

  //   try {
  //     const response = await fetch(`${API_BASE_URL}/auth/verify-credential`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(requestData),
  //     });

  //     const data = await response.json();
  //     if (response.ok && data.success) {
  //       // alert("Verification Successful! Please log in.");
  // // Store access token and user data in sessionStorage
  // const accessToken = data.result.token.refresh.token;
  // const user = data.result.user;

  // sessionStorage.setItem("accessToken", accessToken);
  // sessionStorage.setItem("user", JSON.stringify(user));

  // alert("Verification Successful! Redirecting to home page...");

  // // Redirect to home page
  // navigate("/home");
  //     } else {
  //       alert(`Verification failed: ${data.result?.message || "Invalid code. Please try again."}`);
  //     }
  //   } catch (error) {
  //     console.error("Error:", error);
  //     alert("Something went wrong. Please try again.");
  //   }
  // };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!code) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (timer <= 0) {
      toast.error("OTP has expired. Please resend a new OTP.");
      return;
    }

    let requestData = {};

    if (selectedMethod === "email") {
      // Verify email OTP
      const storedEmail = sessionStorage.getItem("userEmail");
      if (!storedEmail) {
        toast.error("No email found. Please sign up again.");
        setErrors((prev) => ({
          ...prev,
          email: "No email found. Please sign up again.",
        }));
        return;
      }
      requestData = { email: storedEmail, otp: code };
    } else if (selectedMethod === "phone") {
      // Verify phone OTP
      const formattedMobile = `${formData.countryCode}-${formData.mobile}`;
      requestData = {
        email: formattedMobile,
        otp: code,
        verifying: "mobile_number",
      };
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/verify-credential`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        storeQaOtp(selectedMethod, data);

        if (selectedMethod === "email") {
          // Email verified → allow phone OTP and move user to phone step
          setEmailVerified(true);
          toast.success("✅ Email verified. Now verify your phone.");
          setSelectedMethod("phone");
          setCode(""); // clear input
          return;
        }

        if (selectedMethod === "phone") {
          // ✅ Both verified → save token & navigate
          const accessToken = data.result.token.refresh.token;
          const user = data.result.user;

          startUserSession({
            token: accessToken,
            user,
            profilePictureUrl: user?.profile_picture_url,
          });

          toast.success("Verification Successful!");
          navigate("/select-interests", { replace: true });
        }
      } else {
        const msg = data.result?.message || "Invalid code. Please try again.";
        toast.error(`Verification failed: ${msg}`);
        if (msg.toLowerCase().includes("email"))
          setErrors((prev) => ({ ...prev, email: msg }));
        else if (
          msg.toLowerCase().includes("mobile") ||
          msg.toLowerCase().includes("phone")
        )
          setErrors((prev) => ({ ...prev, mobile: msg }));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "56px",
      borderRadius: "20px",
      border: "0.5px solid #095228",
      padding: "5px",
    }),
    control: (base) => ({
      ...base,
      minWidth: 100,
      minHeight: "100%",
      borderRadius: "12px",
      border: "0.5px solid #095228",
      padding: "10px 5px",
    }),
    option: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Ensures it appears on top
  };

  // Carousel login
  let slides = [loginbanner, loginbannertwo, loginbannerthree, loginbannerfour];
  let slidessm = [loginbannersm, loginbannersm, loginbannersm, loginbannersm];
  const [selectedMethod, setSelectedMethod] = useState("email"); // Default: email
  const [inputValue, setInputValue] = useState(""); // Store email or phone input
  const [emailVerified, setEmailVerified] = useState(false);

  const API_URL = `${API_BASE_URL}/auth`;
  // ✅ Handle Google Login
  const handleGoogleLogin = () => {
    clearUserSession();
    window.location.href = `${API_URL}/google`;
  };

  const fetchUserProfile = async () => {
    const token =
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("accessToken"); // Retrieve token
    console.log("Retrieved Token in home page:", token); // ✅ Log token to verify

    if (!token) {
      console.log("Token not found, waiting...");
      setError("Authentication token not found!");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/user/profile`,
        {
          method: "GET",
          headers: {
            token: `${token}`, // ✅ Ensure proper format
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API Response Status:", response.status); // ✅ Log status
      if (!response.ok) {
        const errorData = await response.json();
        console.error("❌ API Error Data:", errorData);
        setError(errorData.message || "Failed to fetch user profile");
        return;
      }

      const data = await response.json();
      console.log("✅ API Success Response from profile page:", data); // ✅ Log full response
      setUserData(data.result);
      sessionStorage.setItem("user", JSON.stringify(data.result)); // Store user in sessionStorage
      localStorage.setItem("user", JSON.stringify(data.result)); // Store user in localStorage
    } catch (err) {
      console.error("❌ Fetch API Error:", err);
      setError("An error occurred while fetching the profile");
    }
  };
  useEffect(() => {
    // ✅ Parse URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const token = urlParams.get("token");
    // const user = urlParams.get("user");

    console.log("Google Login Response:", { success, token });

    if (success === "true" && token) {
      setToken(token);
      if (getStoredToken() !== token) startUserSession({ token });

      // sessionStorage.setItem("user", decodeURIComponent(user));
      fetchUserProfile();
      // console.log("User Data:", decodeURIComponent(user));
      if (sessionStorage.getItem("user") || localStorage.getItem("user")) {
        navigate(urlParams.get("is_new_user") === "true" ? "/select-interests" : "/home");
      }
    } else {
      console.error("Google Login Failed");
      // navigate("/");
    }
  }, [userData]);

  // ✅ Handle Facebook Login
  const handleFacebookLogin = () => {
    clearUserSession();
    window.location.href = `${API_URL}/facebook`;
  };
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleOTPChange = (index, value) => {
    if (value.length > 1) return;
    const newOTP = [...formData.otp];
    newOTP[index] = value;
    setFormData({ ...formData, otp: newOTP });
  };
  const [suggestions, setSuggestions] = useState([]);

  const generateSuggestions = (input) => {
    if (!input) return []; // No suggestions if input is empty
    return [`@${input}_001`, `@${input}_05`, `@${input}_99`];
  };
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    alphanumeric: false,
    specialChar: false,
  });
  const validatePassword = (password) => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      alphanumeric: /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  // ✅ Validate Confirm Password on Input
  const validateConfirmPassword = (value) => {
    if (!value) {
      setConfirmPasswordError("Confirm password is required.");
    } else if (value !== password) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError(""); // Clear error if valid
    }
    setConfirmPassword(value);
  };

  const languages = ["English", "Spanish", "French", "German", "Chinese"];
  const countries = ["India", "USA", "France", "Germany", "China"];
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [qaOtpValues, setQaOtpValues] = useState({ email: "", mobile: "" });
  // Temporary QA-only visibility for OTP testing; keep always on until backend testing is complete.
  const isQaOtpVisible = true;

  const storeQaOtp = (method, responseData, storeTestOtps = false) => {
    const raw = responseData?.result ?? responseData ?? {};
    const testOtp = raw?.test_otp ?? {};

    if (storeTestOtps) {
      setQaOtpValues({
        email: testOtp.email ? String(testOtp.email) : "",
        mobile: testOtp.mobile_number ? String(testOtp.mobile_number) : "",
      });
      return;
    }

    const value =
      raw?.otp ??
      raw?.code ??
      raw?.email_otp ??
      raw?.mobile_otp ??
      raw?.otp_code ??
      raw?.data?.otp ??
      raw?.data?.code ??
      raw?.data?.email_otp ??
      raw?.data?.mobile_otp ??
      testOtp?.email ??
      testOtp?.mobile_number ??
      testOtp?.mobile ??
      "";

    if (!value) return;

    setQaOtpValues((prev) => ({
      ...prev,
      [method === "email" ? "email" : "mobile"]: String(value),
    }));
  };

  // Function to start the timer
  const startTimer = () => {
    setTimer(300); // Reset timer to 5 minutes
    setCanResend(false); // Disable resend button
    if (timerInterval) clearInterval(timerInterval); // Clear any existing interval

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval); // Stop timer when it reaches 0
          setCanResend(true); // Enable resend button
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setTimerInterval(interval); // Store interval ID
  };
  // Call startTimer when OTP modal is opened
  useEffect(() => {
    if (showOTPModal) {
      startTimer(); // Start timer when OTP modal is shown
    }
  }, [showOTPModal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "dob") {
      validateDOB(value);
    }

    // Clear errors when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    // Username availability check
    if (name === "username") {
      checkUsernameAvailability(value);
    }

    // Password validation
    if (name === "password") {
      validatePassword(value);
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      return;
    }

    // Confirm Password validation
    if (name === "confirmPassword") {
      checkPasswordMatch(value);
    }

    // Additional Validations
    if (name === "firstname" || name === "lastname") {
      const onlyLetters = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: onlyLetters }));

      if (!onlyLetters.trim()) {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name === "firstname" ? "First" : "Last"} name is required`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }

    // Email: required and format validation
    if (name === "email") {
      const trimmed = value.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, email: "Email is required" }));
      } else if (!emailRegex.test(trimmed)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }

    if (name === "dob" && !value.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        dob: "Date of birth is required",
      }));
    }

    if (name === "gender" && !value.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        gender: "Please select your gender",
      }));
    }

    // Mobile: allow only digits, no spaces; enforce 10-15 digits
    if (name === "mobile") {
      const digitsOnly = value.replace(/\D/g, "").trim(); // strip non-digits and spaces
      // limit to 15 digits
      const limited = digitsOnly.slice(0, 15);

      setFormData((prev) => ({ ...prev, mobile: limited }));

      if (limited.length === 0) {
        setErrors((prev) => ({ ...prev, mobile: "Mobile number is required" }));
      } else if (limited.length < 7) {
        setErrors((prev) => ({
          ...prev,
          mobile: "Mobile number must be at least 7 digits",
        }));
      } else {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      }

      return;
    }

    if (name === "termsAccepted" && !checked) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        termsAccepted: "You must accept the Terms & Conditions",
      }));
    }
  };

  // Calculate the maximum allowable date (13 years before today)
  const today = new Date();
  const minAllowedDate = new Date();
  minAllowedDate.setFullYear(today.getFullYear() - 13); // Subtract 13 years
  const maxDOB = minAllowedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD

  const validateDOB = (dob) => {
    setErrors({});
    const selectedDate = new Date(dob);
    const todayDate = new Date();
    minAllowedDate.setFullYear(today.getFullYear() - 13); // Subtract 13 years
    const maxDOB = minAllowedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD

    if (!dob) {
      setErrors((prev) => ({ ...prev, dob: "Date of birth is required." }));
      setIsValid(false);
    } else if (selectedDate > todayDate) {
      setErrors((prev) => ({ ...prev, dob: "Date of birth is required." }));
      setIsValid(false);
    } else if (selectedDate > minAllowedDate) {
      setErrors((prev) => ({
        ...prev,
        dob: "You must be at least 13 years old to register.",
      }));
      setIsValid(false);
    } else {
      setErrors((prev) => ({ ...prev, dob: "" })); // Clear error if valid
      setIsValid(true);
    }
  };

  // Function to check if passwords match
  const checkPasswordMatch = (confirmPassword) => {
    if (confirmPassword !== formData.password) {
      setPasswordMatchError("Passwords do not match!");
    } else {
      setPasswordMatchError(""); // Clear error if passwords match
    }
  };
  const handleSuggestionClick = (suggestion) => {
    setFormData({ ...formData, username: suggestion });
    setSuggestions([]); // Hide suggestions after selection
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    // Check if terms were opened
    if (!termsOpened) {
      // alert("You must read and accept the Terms & Conditions before creating an account.");
      toast.success(
        "✅ You must read and accept the Terms & Conditions before creating an account.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      return;
    }
    let newErrors = {};
    // ✅ Password criteria validation before submission
    // if (
    //   !passwordCriteria.minLength ||
    //   !passwordCriteria.alphanumeric ||
    //   !passwordCriteria.specialChar
    // ) {
    //   newErrors.password =
    //     "Password must meet the criteria: Minimum 8 characters, alphanumeric, and at least one special character.";
    //   setErrors(newErrors);

    //   // Display error and prevent submission
    //   toast.error(
    //     "⚠️ Password must meet the criteria: Minimum 8 characters, alphanumeric, and at least one special character.",
    //     {
    //       position: "top-right",
    //       autoClose: 3000,
    //       style: {
    //         backgroundColor: "#ff4d4f",
    //         color: "#fff",
    //         fontWeight: "bold",
    //       },
    //     }
    //   );
    //   return;
    // }

    // ✅ Proceed with form submission if no password errors
    if (Object.keys(newErrors).length > 0) {
      toast.error("⚠️ Please fill in all required fields correctly!", {
        position: "top-right",
        autoClose: 3000,
        style: {
          backgroundColor: "#ff4d4f",
          color: "#fff",
          fontWeight: "bold",
        },
      });
      return;
    }

    // ✅ Validate Date of Birth before proceeding
    const today = new Date();
    const dobDate = new Date(formData.dob);
    const minAllowedDate = new Date();
    minAllowedDate.setFullYear(today.getFullYear() - 13);

    // ✅ Check for future dates or under 13 years old
    if (accountType === "individual") {
      if (!formData.dob.trim()) {
        newErrors.dob = "Date of birth is required.";
      } else if (dobDate > today) {
        newErrors.dob = "Future dates are not allowed.";
      } else if (dobDate > minAllowedDate) {
        newErrors.dob = "You must be at least 13 years old to register.";
        // ✅ Show toast and set DOB error for under-13 users
        const dobMsg = "You must be at least 13 years old to register.";
        setErrors((prev) => ({ ...prev, dob: dobMsg }));
        toast.error("⚠️ " + dobMsg, {
          position: "top-right",
          autoClose: 3000,
          style: {
            backgroundColor: "#ff4d4f",
            color: "#fff",
            fontWeight: "bold",
          },
        });
      }
      if (!formData.gender.trim())
        newErrors.gender = "Please select your gender";
    } else if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }
    if (!formData.firstname.trim())
      newErrors.firstname = "First name is required";
    if (!formData.lastname.trim())
      newErrors.lastname = "Last name is required";

    // Validate all fields

    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    // Email format validation before submission
    else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (!formData.location.trim())
      newErrors.location = "Please select your location";
    if (!formData.country.trim())
      newErrors.country = "Please select your country";
    // if (!formData.language.trim())
    //   newErrors.language = "Please select your language";

    if (!formData.password.trim()) newErrors.password = "Password is required";
    console.log(formData.password)
    if (formData.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    if (!formData.confirmPassword.trim())
      newErrors.confirmPassword =
        "Confirm  password should be same as password ";
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (!/^\d{7,15}$/.test(formData.mobile))
      newErrors.mobile = "Mobile number must be 7 to 15 digits (numbers only)";
    if (!formData.termsAccepted)
      newErrors.termsAccepted = "You must accept the Terms & Conditions";

    setErrors(newErrors);
    console.log("newErrors", newErrors);

    // If there are errors, prevent submission
    // If there are errors, prevent submission
    if (Object.keys(newErrors).length > 0) {
      toast.error("⚠️ Please fill in all required fields correctly!", {
        position: "top-right",
        autoClose: 3000,
        style: {
          backgroundColor: "#ff4d4f",
          color: "#fff",
          fontWeight: "bold",
        },
      });
      return;
    }
    // ✅ Final DOB check before submission
    if (newErrors.dob) {
      toast.error(newErrors.dob, {
        position: "top-right",
        autoClose: 3000,
        style: {
          backgroundColor: "#ff4d4f",
          color: "#fff",
          fontWeight: "bold",
        },
      });
      console.log("Form submission blocked due to invalid DOB.");
      return; // Prevent submission
    }

    const formattedMobile = `${formData.countryCode}-${formData.mobile}`;

    // Check if terms are accepted
    const termsAccepted = sessionStorage.getItem("termsAccepted");
    if (termsAccepted !== "true") {
      const msg =
        "You must accept the Terms & Conditions before creating an account.";
      setErrors((prev) => ({ ...prev, termsAccepted: msg }));
      toast.error(msg, { position: "top-right", autoClose: 3000 });
      return;
    }
    // Construct the request body
    const requestData = {
      username: formData.username,
      email: formData.email,
      first_name: formData.firstname,
      last_name: formData.lastname,
      date_of_birth: formData.dob,
      mobile_number: formattedMobile,
      gender: formData.gender,
      location: formData.location,
      country: formData.country,
      language: formData.language,
      role: 1,
      is_tc_agreed: formData.termsAccepted,
      password: formData.password,
      account_type: accountType,
      date_of_incorporation: formData.incorporationDate,
      business_name: formData.businessName,
      // Business sign-up asks for the owner's first and last name instead of a separate owner name
      business_owner_name: accountType === "business"
        ? `${formData.firstname.trim()} ${formData.lastname.trim()}`
        : formData.ownerName,
      // terms_url: "https://pipaan.com/terms-conditions", // URL of the terms

      // verification: formData.verification// Convert OTP array to string
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setShowOTPModal(true);
        storeQaOtp(null, data, true);

        console.log("Registration Successful:", data);
        // alert("Registration Successful!");
        toast.success("✅ Registration Successful!");

        sessionStorage.removeItem("signupFormData"); // Clear saved form data

        sessionStorage.removeItem("termsAccepted"); // Clear terms acceptance

        console.log("User Data:", data.result.data);
        // Redirect or update UI if needed
        // Store email or user details for verification
        sessionStorage.setItem("userEmail", formData.email);
        localStorage.setItem("userEmail", formData.email);

        // Redirect to verification page
        // navigate("/verifycodeforsignup");
      } else {
        console.error("Registration Failed:", data);
        const msg =
          data.result?.message ||
          "Email ID or mobile number is already registered. Try with another email or mobile number";
        toast.error(msg);
        if (msg.toLowerCase().includes("email"))
          setErrors((prev) => ({ ...prev, email: msg }));
        if (
          msg.toLowerCase().includes("mobile") ||
          msg.toLowerCase().includes("phone")
        )
          setErrors((prev) => ({ ...prev, mobile: msg }));
        if (msg.toLowerCase().includes("username"))
          setErrors((prev) => ({ ...prev, username: msg }));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prevState) => ({
      ...prevState,
      countryCode: selectedOption.dialCode, // Update the country code
    }));
  };
  const handleTermsClick = () => {
    // Navigate to the Terms & Conditions page
    sessionStorage.setItem("signupFormData", JSON.stringify(formData));
    sessionStorage.setItem("signupAccountType", accountType);
    localStorage.setItem("signupFormData", JSON.stringify(formData));

    navigate("/terms-conditions");
  };
  return (
    <div className="login">
      <div className="container mx-auto flex">
        {/* Left side image */}
        <div className="hidden lg:block lg:w-1/2">
          <div className="carousel-size pb-3 pt-3 ">
            <Carousel slides={slides} />
            {/* <!-- Overlay Image --> */}
            <img src={bannerlayout} alt="Overlay" class="layout" />
          </div>
          <div className="copy-rights py-6 text-center text-sm text-[#095228]">
            © 2025 Nyawoo. All Rights Reserved
          </div>
        </div>

        {/* Right side login form */}
        <div className=" w-full lg:w-1/2 login-content flex flex-col">
          <div className="carousel-d-none-sm md:block lg:hidden ">
            <div className="carousel-size pb-3 pt-3 ">
              <Carousel slides={slides} />
              {/* <!-- Overlay Image --> */}
              <img src={bannerlayout} alt="Overlay" class="layout" />
            </div>
          </div>
          <div className="lg:hidden md:hidden">
            <div className="carousel-size pb-3 pt-3 ">
              <Carousel slides={slidessm} />
              {/* <!-- Overlay Image --> */}
              <img src={bannerlayout} alt="Overlay" class="layout" />
            </div>
          </div>
          <div className="login-form signup-form">
            {/* Language selector */}
            <div className="flex justify-end ">
              <Menu
                as="div"
                className="language-btn relative inline-block text-left"
              >
                <div>
                  {/* <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm align-center  hover:bg-gray-50">
                    English
                    <svg width="10" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.92 0.179932H6.68999H1.07999C0.119992 0.179932 -0.360007 1.33993 0.319993 2.01993L5.49999 7.19993C6.32999 8.02993 7.67999 8.02993 8.50999 7.19993L10.48 5.22993L13.69 2.01993C14.36 1.33993 13.88 0.179932 12.92 0.179932Z" fill="#292D32" />
                    </svg>
                  </MenuButton> */}
                </div>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    <MenuItem>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        {" "}
                      </a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      ></a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      ></a>
                    </MenuItem>
                    <form action="#" method="POST">
                      <MenuItem>
                        <button
                          type="submit"
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                        ></button>
                      </MenuItem>
                    </form>
                  </div>
                </MenuItems>
              </Menu>
            </div>

            {/* Main content */}
            <div className="mobile-ver flex-1 flex flex-col items-center justify-center max-w-[440px] mx-auto w-full">
              {/* Logo */}
              <div className="mb-8">
                <img src={logo} alt="Nyawoo Logo" width={150} height={50} />
              </div>
              {/* Login form */}
              <div className="w-full space-y-6 ">
                <h1 className="text-3xl font-semibold font-semibold  text-center">
                  Get Started
                </h1>
                <h1 className="text-center">
                  <span className="text-gray-600">Welcome to </span>{" "}
                  <span className="text-black">Nyawoo</span> -{" "}
                  <span className="text-gray-600">
                    Lets Create your Account
                  </span>
                </h1>
                <div className="grid grid-cols-2 gap-4 mb-10 mx-auto w-fit">
                  <button
                    className="w-full rounded-lg p-2"
                    onClick={handleGoogleLogin}
                  >
                    <img src={google} alt="Google" className=" mx-auto" />
                  </button>
                  <button
                    className="w-full rounded-lg p-2"
                    onClick={handleFacebookLogin}
                  >
                    <img src={facebook} alt="Facebook" className=" mx-auto" />
                  </button>
                </div>
                <div className="relative mt-6 mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t-2 border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-[#FAFAFA] px-2 text-gray-500 ">Or</span>
                  </div>
                </div>
                <div className="mx-auto py-6 sm:p-6">
                  {/* ===== Account Type Tabs ===== */}
                  <div className="flex gap-4 mb-8">
                    {/* Individual */}
                    <label
                      onClick={() => setAccountType("individual")}
                      className={`cursor-pointer flex-1 min-w-0 rounded-xl border text-[#0D0B0C]  p-4 gap-2
      ${
        accountType === "individual"
          ? "border-[#095228] bg-green-50"
          : "border-gray-300"
      }`}
                    >
                      {/* Radio */}
                      <input
                        type="radio"
                        name="accountType"
                        checked={accountType === "individual"}
                        onChange={() => setAccountType("individual")}
                        className="accent-[#095228]"
                      />
                      <div className="flex flex-col items-center">
                        <img
                          className="w-8 h-8"
                          src={individualicon}
                          alt="Individual"
                        />
                        <span className="font-medium text-center">
                          Individual Account
                        </span>
                      </div>
                    </label>

                    {/* Business */}
                    <label
                      onClick={() => setAccountType("business")}
                      className={`cursor-pointer flex-1 min-w-0 rounded-xl border text-[#0D0B0C]  p-4 gap-2
      ${
        accountType === "business"
          ? "border-[#095228] bg-green-50"
          : "border-gray-300"
      }`}
                    >
                      {/* Radio */}
                      <input
                        type="radio"
                        name="accountType"
                        checked={accountType === "business"}
                        onChange={() => setAccountType("business")}
                        className="accent-[#095228]"
                      />
                      <div className="flex flex-col items-center">
                        <img
                          className="w-8 h-8"
                          src={businessicon}
                          alt="Business"
                        />
                        <span className="font-medium text-center">
                          Business Account
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* ===== Render Forms ===== */}
                  {accountType === "individual" && (
                    <IndividualAccount
                      formData={formData}
                      setFormData={setFormData}
                      handleChange={handleChange}
                      errors={errors}
                      handleSubmitForm={handleSubmit}
                    />
                  )}

                  {accountType === "business" && (
                    <BusinessAccount
                      formData={formData}
                      setFormData={setFormData}
                      handleChange={handleChange}
                      errors={errors}
                      handleSubmitForm={handleSubmit}
                    />
                  )}
                </div>

                <div className="agree-terms flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="termsAccepted"
                      id="termsAccepted"
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded-full focus:ring-blue-500"
                    />
                    <label
                      htmlFor="termsAccepted"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      By signing up you agree to our{" "}
                      <span
                        className="text-[#006838] underline"
                        onClick={handleTermsClick} // Navigate to Terms & Conditions page
                      >
                        Terms & Conditions
                      </span>
                    </label>
                  </div>
                </div>
                {errors.termsAccepted && (
                  <p className="text-red-500 text-sm">{errors.termsAccepted}</p>
                )}
                <div className="text-center">
                  {/* <button
                    onClick={handleSubmit}
                    className="creat-account-cta submit-btn h-[56px]  bg-[#161F28] hover:bg-[#1B2537]/90 text-white py-3 rounded-xl"
                  >
                    Create Account
                  </button> */}

                  {showOTPModal && (
                    <div className="fixed inset-0 flex items-center justify-center  bg-[#fff] backdrop-blur bg-opacity-50 z-[9999]">
                      <div className="bg-white p-6  md:pl-[5%] md:pr-[5%] md:pb-[3%] rounded-lg shadow-lg relative relative border border-[#09522880]">
                        <button
                          className="hidden md:block absolute top-2 right-2 text-[#fff] hover:text-[#fff] bg-[#095228] rounded-full p-1 pl-2.5 pr-2.5"
                          onClick={() => setShowOTPModal(false)}
                        >
                          ✖
                        </button>

                        <h2 className="login-title text-center mt-2 mb-6 ">
                          OTP Verification
                        </h2>

                        <form
                          onSubmit={handleSubmitVerify}
                          className="space-y-4 frg-pass-content"
                        >
                          <div className="flex justify-around space-x-4 mb-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="recoveryMethod"
                                value="email"
                                checked={selectedMethod === "email"}
                                onChange={() => setSelectedMethod("email")}
                                className="w-4 h-4 border-[#095228] rounded-full focus:ring-green-500"
                              />
                              <span className="text-base text-[#344054]">
                                Email
                              </span>
                            </label>

                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="recoveryMethod"
                                value="phone"
                                checked={selectedMethod === "phone"}
                                onChange={() => {
                                  if (emailVerified) {
                                    setSelectedMethod("phone");
                                  } else {
                                    toast.error(
                                      "Please verify your email OTP before switching to mobile verification."
                                    );

                                    setSelectedMethod("email");
                                  }
                                }}
                                disabled={!emailVerified}
                                className="w-4 h-4"
                              />
                              <span className="text-base text-[#344054]">
                                Phone No
                                {!emailVerified && (
                                  <span className="text-xs text-red-500 ml-2">
                                    {" "}
                                    (verify email first)
                                  </span>
                                )}
                              </span>
                            </label>
                          </div>

                          <div>
                            <label className="text-[14px] text-[#80807F] block mt-5 mb-2">
                              Enter the verification code we just sent to your
                              {selectedMethod === "email"
                                ? "Email"
                                : "Phone Number"}
                            </label>
                            <input
                              placeholder={
                                selectedMethod === "email"
                                  ? "Enter Verification Code"
                                  : "Enter Verification Code"
                              }
                              value={code}
                              maxLength={6}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                if (val.length <= 6) setCode(val);
                              }}
                              className="mb-4 w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200 outline-none"
                              required
                            />
                          </div>

                          <div className="vrfy-code-valid ">
                            <p className="text-lg font-bold text-[#BE1F2E] text-center">
                              {formatTime(timer)}
                            </p>
                          </div>

                          {isQaOtpVisible && (
                            <div className="rounded-xl border border-yellow-300 bg-yellow-50 px-3 py-2 text-left text-[12px] text-[#36312d]">
                              <p className="mb-1 font-semibold text-[#A16207]">
                                QA OTP (development only)
                              </p>
                              <p>
                                Email OTP: <span className="font-bold">{qaOtpValues.email || "Not returned by API"}</span>
                              </p>
                              <p>
                                Mobile OTP: <span className="font-bold">{qaOtpValues.mobile || "Not returned by API"}</span>
                              </p>
                            </div>
                          )}

                          <p className=" text-center text-sm text-[#313131] ">
                            Didn’t receive a code?{" "}
                            <button
                              onClick={handleResendOTP}
                              className="text-[#095228] font-medium hover:underline"
                            >
                              Resend
                            </button>
                          </p>
                          <button
                            type="submit"
                            onClick={handleVerifyOTP}
                            className="w-full bg-[#80807F] hover:bg-[#1B2537]/90 text-white py-3 rounded-xl font-medium mb-10"
                          >
                            Verify
                          </button>
                          <button
                            className="md:hidden w-full bg-[#161F28] hover:bg-[#1B2537]/90 text-white py-3 rounded-xl font-medium mb-10"
                            onClick={() => setShowOTPModal(false)}
                          >
                            Cancel
                          </button>
                        </form>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-center text-sm text-[#3A3A3C]">
                  Already have an account?{" "}
                  <a
                    href="/login"
                    className="text-[#006838] hover:underline font-medium"
                  >
                    {" "}
                    Log in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
